import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, Link as LinkIcon, Plus, Search } from 'lucide-react';
import { Loader } from '../../../components/ui';
import { Topic } from '../../../types/topic';
import { FormattedBookmark } from '../../../hooks/queries/bookmarkTypes';
import { BookmarkCard } from './BookmarkCard';
import { EmptyBookmarks } from './EmptyBookmarks';
import { logger } from '../../../utils/logger';
import { useViewport } from '../../../contexts/ViewportContext';
import { useStatusBarInfo } from '../../../hooks/useStatusBarInfo';
import { useInitialLoadingScreenManager } from '../../../hooks/useInitialLoadingScreenManager';
import {
  useBookmarks,
  useAddBookmark,
  useDeleteBookmark,
} from '../../../hooks/queries/useBookmarks';
import { useTopicSubscription } from '../../../hooks/useTopicSubscription';
import { bookmarkKeys } from '../../../hooks/queries/bookmarkKeys';
import { useBookmarkUIStore } from '../../../store/bookmarkUIStore';

interface BookmarksTopicProps {
  topicId: string;
  topic?: Topic; // Make topic optional since it's not being passed currently
  hasCollaborators?: boolean;
  onBackClick?: () => void;
}

export const BookmarksTopic: React.FC<BookmarksTopicProps> = ({ topicId, topic, onBackClick }) => {
  const { isMobile } = useViewport();
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();

  // React Query hooks for data fetching and mutations
  const bookmarksQuery = useBookmarks(topicId);
  const addBookmarkMutation = useAddBookmark();
  const deleteBookmarkMutation = useDeleteBookmark();

  // Set up real-time subscriptions for bookmarks
  // Memoize subscriptions to prevent unnecessary re-subscriptions
  const subscriptions = useMemo(
    () => [
      {
        table: 'bookmarks' as const,
        queryKey: bookmarkKeys.byTopic(topicId),
      },
    ],
    [topicId]
  );

  useTopicSubscription(topicId, subscriptions);

  // Get UI state from bookmarkUIStore
  const {
    searchTerm,
    setSearchTerm,
    isSubmitting,
    setIsSubmitting,
    errorMessage: error,
    setErrorMessage: setError,
  } = useBookmarkUIStore();

  // Local UI state
  const [url, setUrl] = useState('');
  const [filteredBookmarks, setFilteredBookmarks] = useState<FormattedBookmark[]>([]);

  // Use the bookmarks data directly with proper type guard
  const bookmarks = useMemo(
    () => (Array.isArray(bookmarksQuery.data) ? (bookmarksQuery.data as FormattedBookmark[]) : []),
    [bookmarksQuery.data]
  );

  const loading = bookmarksQuery.isLoading;
  const storeError = bookmarksQuery.error
    ? bookmarksQuery.error instanceof Error
      ? bookmarksQuery.error.message
      : 'Error loading bookmarks'
    : null;

  // Create a mock topic if real topic not provided
  const topicForRefresh = topic || (topicId ? ({ id: topicId, type: 'bookmarks' } as Topic) : null);

  // Use loading screen manager to mark content loaded when ready
  const { markRouteComplete } = useInitialLoadingScreenManager();

  // Only mark the topic content as loaded when loading is complete
  // Using a ref to track if we've already marked complete to avoid infinite updates
  const hasMarkedCompleteRef = useRef(false);
  useEffect(() => {
    if (topicForRefresh && !loading && !hasMarkedCompleteRef.current) {
      // Content is loaded, mark it complete - but only once
      hasMarkedCompleteRef.current = true;
      markRouteComplete();
    }
  }, [loading, topicForRefresh]); // Remove markRouteComplete from deps to prevent infinite updates

  // Filter bookmarks when search term or bookmarks change
  useEffect(() => {
    if (!searchTerm) {
      setFilteredBookmarks(bookmarks);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = bookmarks.filter(
      bookmark =>
        bookmark.title?.toLowerCase().includes(searchLower) ||
        bookmark.description?.toLowerCase().includes(searchLower) ||
        bookmark.url.toLowerCase().includes(searchLower) ||
        bookmark.summary?.toLowerCase().includes(searchLower)
    );

    setFilteredBookmarks(filtered);
  }, [searchTerm, bookmarks]);

  const handleAddBookmark = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    // Simple URL validation
    let processedUrl = url.trim();
    if (!processedUrl.startsWith('http://') && !processedUrl.startsWith('https://')) {
      processedUrl = 'https://' + processedUrl;
    }

    try {
      new URL(processedUrl);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await addBookmarkMutation.mutateAsync({
        topicId,
        url: processedUrl,
      });
      setUrl(''); // Clear the input after successful add
      setIsSubmitting(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add bookmark');
      setIsSubmitting(false);
    }
  };

  const handleDeleteBookmark = async (bookmarkId: string) => {
    try {
      // Note: No longer tracking local deletes - using invalidation approach

      // Delete using the mutation
      await deleteBookmarkMutation.mutateAsync({
        topicId,
        bookmarkId,
      });
    } catch (err) {
      logger.error('Error deleting bookmark', err);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Mobile header */}
      {isMobile && onBackClick && (
        <div
          className="flex items-center h-14 px-4"
          style={{
            paddingTop: isMobile && isNativeApp ? `${statusBarHeight}px` : '0',
            height: isMobile && isNativeApp ? `calc(3.5rem + ${statusBarHeight}px)` : '3.5rem',
          }}
        >
          <div className="flex-none">
            <button onClick={onBackClick} className="p-2 -ml-2 text-gray-600 hover:text-gray-900">
              <ChevronLeft size={24} />
            </button>
          </div>

          <div className="absolute left-1/2 -translate-x-1/2">
            <span className="text-base font-semibold text-gray-900 whitespace-nowrap">
              {topic?.name || 'Bookmarks'}
            </span>
          </div>

          <div className="flex-none w-10" />
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 overflow-y-auto py-4 px-6">
        {/* Header with search and URL input */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row gap-4 mb-2">
            {/* Search field */}
            <div className="relative flex-1 min-w-0">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search bookmarks..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full bg-white rounded-full pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow"
              />
            </div>

            {/* Add URL form */}
            <form onSubmit={handleAddBookmark} className="flex flex-1 min-w-0 space-x-2">
              <div className="relative flex-grow">
                <LinkIcon
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={18}
                />
                <input
                  type="text"
                  placeholder={isMobile ? 'Enter URL...' : 'Enter URL to bookmark...'}
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  className="w-full bg-white rounded-full pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 shadow"
                  disabled={isSubmitting}
                />
              </div>
              <button
                type="submit"
                className="flex-shrink-0 flex items-center justify-center gap-1 bg-blue-500 hover:bg-blue-600 text-white rounded-full px-6 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                <Plus size={18} />
                <span>{isSubmitting ? 'Adding...' : 'Add'}</span>
              </button>
            </form>
          </div>

          {/* Error message */}
          {error && <div className="text-sm text-red-600 px-2">{error}</div>}
        </div>

        {/* Store error message */}
        {storeError && (
          <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {storeError}
          </div>
        )}

        {/* Loading state - standardized approach */}
        {loading && (
          <div className="h-64 w-full flex items-center justify-center bg-gray-50 rounded-lg">
            <Loader color="text-blue-500" size={32} />
          </div>
        )}

        {/* Empty state - show when not loading, have no bookmarks, and no search */}
        {!loading && bookmarks.length === 0 && !searchTerm && <EmptyBookmarks />}

        {/* Bookmark grid - only show when we have filtered results and not loading */}
        {!loading && filteredBookmarks.length > 0 && (
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBookmarks.map(bookmark => (
                <BookmarkCard
                  key={bookmark.id}
                  bookmark={bookmark}
                  onDelete={() => handleDeleteBookmark(bookmark.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* No results after search - only show if we're not loading, have a search term, 
             have no filtered results but do have bookmarks (meaning the search returned nothing) */}
        {!loading && searchTerm && filteredBookmarks.length === 0 && bookmarks.length > 0 && (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
            <Search size={48} className="mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No matching bookmarks</h3>
            <p>Try a different search term</p>
          </div>
        )}
      </div>
    </div>
  );
};
